import { ProgramDecision } from "../../../../../common/api/decimal/methods/course.ts";
import { useCoursesLoaderData } from "../coursesLoader.ts";
import { FormattedMessage, useIntl } from "react-intl";
import { useRef } from "react";
import { Dialog } from "../../../../../common/core-ui/Dialog.tsx";
import styles from "./ProgramSwitcherDialog.module.scss";
import { clsx } from "clsx";
import MiscellaneousCheckmarkSm from "../../../../../common/icons/MiscellaneousCheckmarkSm.tsx";
import Markdown from "react-markdown";
import useTermOptDeadline from "../useTermOptDeadline.tsx";
import useSwitchProgram from "../useSwitchProgram.tsx";
import DaysRemainingMessage from "./DaysRemainingMessage.tsx";
import { useLayoutData } from "../../layout/myCoursesLayoutLoader.ts";
import BaseLinkOrButton from "common/core-ui/BaseLinkOrButton.tsx";

const buildCheckboxId = (plan: string) => `${plan}-checkbox`;
const buildDetailsId = (plan: string) => `${plan}-details`;

interface Link {
  label: string;
  href: string;
}

interface Plan {
  type: ProgramDecision;
  links: Link[];
}

function PlanLink({ label, href }: Link) {
  return (
    <BaseLinkOrButton
      className={clsx(
        "vst-button-pill",
        "vst-button-variant-outline",
        "vst-button-size-small"
      )}
      href={href}
    >
      {label}
    </BaseLinkOrButton>
  );
}

export default function ProgramSwitcherDialog({
  onRequestClose,
  onShowConfirm,
}: {
  onRequestClose: () => void;
  onShowConfirm: () => void;
}) {
  const courseData = useCoursesLoaderData();
  const { config } = useLayoutData();
  const institutionProgram = courseData.currentTerm?.institutionProgram;
  const programDecision = courseData.currentTerm?.programDecision;
  const intl = useIntl();
  const choicesRef = useRef<HTMLDivElement>(null);
  const { optDeadline } = useTermOptDeadline();
  // This does not use a Form because it leads to divergent handling between
  // submittable form radio choices and the separate opt-out button (which
  // is inside the form but not submitted in the same way)
  const { changeProgram, isProgramChangeProcessing } =
    useSwitchProgram(onRequestClose);

  const getEquitableAccessOnlyLinks = (): Link[] => {
    const links = [];
    if (config?.equitable_access?.akademos?.updateShippingLink) {
      links.push({
        label: intl.formatMessage({
          defaultMessage: "Update Shipping Address",
        }),
        href: config.equitable_access.akademos.updateShippingLink,
      });
    }
    if (config?.equitable_access?.akademos?.personalizedValueSheet) {
      links.push({
        label: intl.formatMessage({
          defaultMessage: "Personalized Value Sheet",
        }),
        href: config.equitable_access.akademos.personalizedValueSheet,
      });
    }
    return links;
  };

  const plans = [] as Plan[];
  if (institutionProgram === "equitable_access") {
    plans.push(
      { type: "equitable_access", links: getEquitableAccessOnlyLinks() },
      { type: "no_program", links: [] }
    );
  } else if (institutionProgram === "flexible_access") {
    plans.push(
      { type: "equitable_access", links: [] },
      { type: "inclusive_access", links: [] }
    );
  }

  const clickHandler = (plan: string) => {
    const checkbox = document.getElementById(
      buildCheckboxId(plan)
    ) as HTMLInputElement;
    if (checkbox) {
      checkbox.checked = true;
    }
  };

  return (
    <Dialog
      canClose={!isProgramChangeProcessing}
      darkHeader
      onRequestClose={onRequestClose}
      title={intl.formatMessage({
        defaultMessage: "Choose Course Materials Program",
      })}
      customWidth="700px"
      primaryButton={{
        loading: isProgramChangeProcessing,
        loadingContext: intl.formatMessage({
          defaultMessage: "Course Materials Program",
        }),
        label: intl.formatMessage({ defaultMessage: "Confirm Program Choice" }),
        onClick: () => {
          // I have seen a11y concerns with having the submit button go disabled.
          // Let the loader button do its thing, but prevent duplicate submissions
          if (isProgramChangeProcessing) {
            return;
          }

          const selectedProgram: ProgramDecision =
            choicesRef.current!.querySelector<HTMLInputElement>(
              "input:checked"
            )!.value as ProgramDecision;
          if (programDecision !== selectedProgram) {
            if (selectedProgram === "no_program") {
              onShowConfirm();
            } else {
              changeProgram(selectedProgram);
            }
          } else {
            onRequestClose();
          }
        },
      }}
      secondaryButton={{
        label: intl.formatMessage({ defaultMessage: "Cancel" }),
        onClick: onRequestClose,
        disabled: isProgramChangeProcessing,
      }}
    >
      {optDeadline && (
        <div className={styles.header}>
          {programDecision === "no_program" ? (
            <>
              <FormattedMessage defaultMessage="You are currently opted out of all course materials programs." />
              <DaysRemainingMessage type="decision" />
            </>
          ) : (
            <DaysRemainingMessage type="program-choice" />
          )}
        </div>
      )}
      <div className={styles.choices} ref={choicesRef}>
        {plans.map(({ type, links }) => (
          // This clickable div is intentional. We should allow this div to be clicked and
          // remove tab focus so that focus will move to the radio button instead
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
          <div
            className={styles.radioBoxOuter}
            key={type}
            onClick={() => clickHandler(type)}
          >
            <span className={clsx("vst-radio-check", styles.radioInput)}>
              <input
                id={buildCheckboxId(type)}
                name="program"
                type="radio"
                value={type}
                defaultChecked={programDecision === type}
                disabled={isProgramChangeProcessing}
                aria-describedby={buildDetailsId(type)}
              />
              <label
                htmlFor={buildCheckboxId(type)}
                className={styles.planHeader}
              >
                {type === "no_program" ? (
                  <FormattedMessage defaultMessage={"Opt Out"} />
                ) : (
                  config[type].programName
                )}
              </label>
            </span>
            <div className={styles.planBody}>
              {type === "no_program" ? (
                <>
                  {config.equitable_access.optOutMessage && (
                    <div className={styles.planDetails}>
                      {config.equitable_access.optOutMessage}
                    </div>
                  )}
                </>
              ) : (
                <ul id={buildDetailsId(type)} className={styles.planDetails}>
                  {config[type].features.map((feature) => (
                    <li key={feature.text} className="vst-icon-with-text">
                      {/* Custom bullet available in feature data, but not supported yet */}
                      <MiscellaneousCheckmarkSm />
                      <Markdown
                        allowedElements={["strong", "em", "a", "span", "p"]}
                        components={{
                          // Need outer wrapper element, but you can't put a p in a label,
                          // nor do we want the default p margins
                          p: "span",
                          a: (props) => (
                            // External content and provided by spread
                            // eslint-disable-next-line jsx-a11y/anchor-has-content
                            <a
                              className="vst-link"
                              target="_blank"
                              rel="noopener noreferrer"
                              {...props}
                            />
                          ),
                        }}
                      >
                        {/* remark-variables looks like it would do this, but seems unsupported/unmaintained */}
                        {/* Dynamic price source TBD */}
                        {feature.text.replace("{{price}}", "UNKNOWN PRICE")}
                      </Markdown>
                    </li>
                  ))}
                </ul>
              )}
              {links.length > 0 && (
                <div className={styles.planLinks}>
                  {links.map((link) => (
                    <PlanLink key={link.label} {...link} />
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      {institutionProgram === "flexible_access" &&
        programDecision !== "no_program" && (
          <div className={styles.footer}>
            <FormattedMessage
              defaultMessage="If you prefer to purchase your course materials on your own, you can <button>opt out of the programs</button>."
              values={{
                button: (text) => (
                  <button
                    onClick={onShowConfirm}
                    className="vst-link vst-link-size-inherit"
                    disabled={isProgramChangeProcessing}
                  >
                    {text}
                  </button>
                ),
              }}
            />
          </div>
        )}
    </Dialog>
  );
}
